import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import logo from "../assets/logo.png";
import { usePageMetadata } from "../context/PageMetadataContext";
import { useLanguage, Language, languageNames, languageCodes } from "../context/LanguageContext";
// import AdUnit from "./AdUnit";

const Navbar: React.FC = () => {
  // Add language context
  const { language, setLanguage, t } = useLanguage();
  
  // Add state for language menu
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showStatsMenu, setShowStatsMenu] = useState(false);
  const [showNodesMenu, setShowNodesMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const menuRef = useRef<HTMLDivElement>(null);
  const statsMenuRef = useRef<HTMLLIElement>(null);
  const nodesMenuRef = useRef<HTMLLIElement>(null);
  const langMenuRef = useRef<HTMLLIElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { heading } = usePageMetadata();

  // Toggle language menu function
  const toggleLangMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLangMenu(!showLangMenu);
  };

  // Toggle submenu function
  const toggleStatsMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowStatsMenu(!showStatsMenu);
  };

  // Toggle nodes menu function
  const toggleNodesMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowNodesMenu(!showNodesMenu);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statsMenuRef.current && !statsMenuRef.current.contains(event.target as Node)) {
        setShowStatsMenu(false);
      }
      if (nodesMenuRef.current && !nodesMenuRef.current.contains(event.target as Node)) {
        setShowNodesMenu(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setShowLangMenu(false);
      }
    };

    if (showStatsMenu || showLangMenu || showNodesMenu) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showStatsMenu, showLangMenu, showNodesMenu]);

  // Check if current path is a statistics page
  const isStatsPage = location.pathname === '/account-stats' || location.pathname === '/unlock-statistics';

  // Toggle menu function
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle search logic
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const query = searchQuery.trim();
    const navigateTo = (path: string) => {
      setIsMenuOpen(false); // Close menu when searching
      navigate(path);
    };

    if (/^\d+$/.test(query)) {
      navigateTo(`/block/${query}`);
    } else if (/^[0-9a-fA-F]{64}$/.test(query)) {
      navigateTo(`/tx/${query}`);
    } else if (/^G[A-Z0-9]{55}$/.test(query)) {
      navigateTo(`/account/${query}`);
    } else {
      navigateTo("/404");
    }
  };

  // Handle language change
  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setShowLangMenu(false);
  };

  return (
    <>
      <div 
        ref={headerRef}
        className="header-container"
      >
        <nav className="navbar navbar-expand-lg">
          <div className="container">
            <Link className="navbar-brand text-white fw-bold d-flex align-items-center" to="/">
              <img src={logo} alt="Piscan Logo" height="40" className="me-2" />
              PiScan
            </Link>
            
            <button 
              className="navbar-toggler" 
              type="button" 
              aria-controls="navbarNav" 
              aria-expanded={isMenuOpen ? "true" : "false"} 
              aria-label="Toggle navigation"
              onClick={toggleMenu}
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className={`collapse navbar-collapse ${isMenuOpen ? "show" : ""}`} id="navbarNav" ref={menuRef}>
              <ul className="navbar-nav ms-auto">
                <li className="nav-item"><Link className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} to="/" onClick={() => setIsMenuOpen(false)}>{t('nav.home')}</Link></li>
                <li className="nav-item"><Link className={`nav-link ${location.pathname === '/core-team-cex-statistics' ? 'active' : ''}`} to="/core-team-cex-statistics" onClick={() => setIsMenuOpen(false)}>{t('nav.cexs')}</Link></li>
                <li className="nav-item"><Link className={`nav-link ${location.pathname === '/operations' ? 'active' : ''}`} to="/operations" onClick={() => setIsMenuOpen(false)}>{t('nav.operations')}</Link></li>
                <li className="nav-item"><Link className={`nav-link ${location.pathname === '/txs' ? 'active' : ''}`} to="/txs" onClick={() => setIsMenuOpen(false)}>{t('nav.transactions')}</Link></li>
                <li className="nav-item"><Link className={`nav-link ${location.pathname === '/blocks' ? 'active' : ''}`} to="/blocks" onClick={() => setIsMenuOpen(false)}>{t('nav.blocks')}</Link></li>
                {/* Nodes dropdown */}
                <li className="nav-item dropdown" ref={nodesMenuRef}>
                  <a href="#" className={`nav-link dropdown-toggle ${location.pathname === '/nodes' || location.pathname === '/nodes-testnet' ? 'active' : ''}`} onClick={toggleNodesMenu}>
                    {t('nav.nodes')}
                  </a>
                  <div className={`dropdown-menu ${showNodesMenu ? 'show' : ''}`}>
                    <Link 
                      to="/nodes" 
                      className="dropdown-item"
                      onClick={() => {
                        setShowNodesMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('nav.nodes_mainnet')}
                    </Link>
                    <Link 
                      to="/nodes-testnet" 
                      className="dropdown-item"
                      onClick={() => {
                        setShowNodesMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('nav.nodes_testnet')}
                    </Link>
                  </div>
                </li>
                
                {/* Statistics dropdown */}
                <li className="nav-item dropdown" ref={statsMenuRef}>
                  <a 
                    href="#" 
                    className={`nav-link dropdown-toggle ${isStatsPage ? 'active' : ''}`} 
                    onClick={toggleStatsMenu}
                  >
                    {t('nav.stats')}
                  </a>
                  <div className={`dropdown-menu ${showStatsMenu ? 'show' : ''}`}>
                    <Link 
                      to="/account-stats" 
                      className="dropdown-item"
                      onClick={() => {
                        setShowStatsMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('stats.accounts')}
                    </Link>
                    <Link 
                      to="/unlock-statistics" 
                      className="dropdown-item"
                      onClick={() => {
                        setShowStatsMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('stats.unlocks')}
                    </Link>
                    <Link 
                      to="/unlock-statistics-monthly"
                      className="dropdown-item"
                      onClick={() => {
                        setShowStatsMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('stats.unlocks_monthly')}
                    </Link>
                    <Link 
                      to="/domain-auction-stats"
                      className="dropdown-item"
                      onClick={() => {
                        setShowStatsMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('stats.domain_auction')}
                    </Link>
                    <Link 
                      to="/migrate-statistics"
                      className="dropdown-item"
                      onClick={() => {
                        setShowStatsMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('stats.migrate')}
                    </Link>
                    <Link 
                      to="/largest-tx"
                      className="dropdown-item"
                      onClick={() => {
                        setShowStatsMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('stats.largest_tx')}
                    </Link>
                    <Link 
                      to="/txs/filter"
                      className="dropdown-item"
                      onClick={() => {
                        setShowStatsMenu(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      {t('transactions_filter.heading')}
                    </Link>
                    
                  </div>
                </li>
                
                {/* Language selector */}
                <li className="nav-item dropdown" ref={langMenuRef}>
                  <a 
                    href="#" 
                    className="nav-link dropdown-toggle" 
                    onClick={toggleLangMenu}
                  >
                    {languageCodes[language]} {languageNames[language]}
                  </a>
                  <div className={`dropdown-menu dropdown-menu-end ${showLangMenu ? 'show' : ''}`}>
                    {(Object.keys(languageNames) as Language[]).map((lang) => (
                      <button 
                        key={lang}
                        className={`dropdown-item ${language === lang ? 'active' : ''}`}
                        onClick={() => handleLanguageChange(lang)}
                      >
                        {languageCodes[lang]} {languageNames[lang]}
                      </button>
                    ))}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </nav>

        {/* Page Heading & Search Box - now inside the header container */}
        <div className="page-header">
          <div className="container">
            <div className="row align-items-center py-3">
              <div className="col-md-7">
                <h2 className="page-title text-white long-text">{heading}</h2>
              
                <form className="search-form" onSubmit={handleSearch}>
                  <input 
                    type="text" 
                    className="search-input" 
                    placeholder={t('search.placeholder')} 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button type="submit" className="search-button">
                    {FiSearch({size: 18})}
                  </button>
                </form>
              </div>
              
              { <div className="col-md-5"> 
                <div className="d-none d-md-flex justify-content-end">
                {/* <AdSenseHeader /> */}
                {/* <BannerHeader /> */}
                {/* <BannerHeaederFixed /> */}
                {/* <AdUnit /> */}
                </div>
              </div> }
            </div>
          </div>
        </div>
      </div>

      

      
    </>
  );
};

export default Navbar;
